import { useCallback, useEffect, useRef, useState } from "react";
import type { Analysis, Slice, Verdict } from "../../shared/analysis.ts";
import { COLORS, textOn, type ColorId } from "../../shared/colors.ts";
import type { MeInfo } from "../lib/client.ts";
import type { Nav } from "../main.tsx";
import { Icon } from "./Room.tsx";

const pct = (x: number, d = 0) => `${(x * 100).toFixed(d)}%`;
const fmtE = (e: number) => (e >= 1000 ? Math.round(e).toLocaleString("en-US") : e >= 10 ? e.toFixed(0) : e >= 0.1 ? e.toFixed(1) : e.toFixed(2));
const fmtP = (p: number | null) => (p === null ? "n/a" : p < 0.001 ? "<.001" : p.toFixed(3).replace(/^0/, ""));

export function Dashboard({ me, nav }: { me: MeInfo; nav: Nav }) {
  const [person, setPerson] = useState("all");
  const [tier, setTier] = useState<"lab" | "cards" | "both">("lab");
  const [data, setData] = useState<Analysis | null>(null);
  const [stale, setStale] = useState(false);

  const load = useCallback(async () => {
    setStale(true);
    const r = await fetch(`/api/analysis?person=${person}&tier=${tier}`);
    if (r.ok) setData(await r.json());
    setStale(false);
  }, [person, tier]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === "visible") void load(); }, 15000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return <main className="dash"><p className="eyebrow">Reading the log</p></main>;
  const verdict = data.verdicts.find((v) => v.scope === person) ?? data.verdicts.at(-1)!;
  const s = data.slice;

  return (
    <main className={stale ? "dash stale" : "dash"} data-testid="dashboard">
      <section className="hero">
        <div className="hero-main">
          <p className="eyebrow">{verdict.scope === "all" ? "Is it happening? Both of you" : `Is it happening? ${verdict.name}`}</p>
          <h1 className={`verdict level-${verdict.level}`} data-testid="verdict">{verdict.headline}</h1>
          <p className="lede">{verdict.subline}</p>
          {data.totals.trials === 0 && <button className="btn primary big" onClick={() => nav("/room")}>Start your first session</button>}
        </div>
        <div className="people" role="tablist" aria-label="Whose results">
          {data.verdicts.map((v) => (
            <button key={v.scope} role="tab" aria-selected={person === v.scope} className={person === v.scope ? "person on" : "person"} onClick={() => setPerson(v.scope)} data-testid={`person-${v.scope}`}>
              <span className="person-name">{v.name}</span>
              <span className="person-rate">{v.n ? pct(v.rate) : "–"}<small>{v.n ? ` vs ${pct(v.chance)} chance` : ""}</small></span>
              <Meter value={v.evidence} bar={v.bar} />
              <span className="person-foot">Evidence {fmtE(v.evidence)} · {v.n} trials</span>
            </button>
          ))}
        </div>
      </section>

      <Ladder v={verdict} nav={nav} />

      <div className="filters">
        <div className="seg" aria-label="Which data">
          {([["lab", "Phone card"], ["cards", "Physical cards"], ["both", "Both"]] as const).map(([id, label]) => (
            <button key={id} className={tier === id ? "on" : ""} onClick={() => setTier(id)} data-testid={`tier-${id}`}>{label}</button>
          ))}
        </div>
        <span className="filter-note">
          {tier === "lab" ? `Test trials on the phone card.${verdict.beforeAttack ? ` Includes ${verdict.beforeAttack} from before a rig attack, which the verdict ignores.` : ""} P-values here are descriptive, and the evidence score is the test.`
            : tier === "cards" ? "Physical cards. Tracked, but not part of the verdict."
            : "Everything together, for exploring only."}
        </span>
        <a className="btn small ghost" href="/api/export.csv" download>Export CSV</a>
      </div>

      {s.n === 0 ? (
        <section className="section"><p className="lede">No scored trials in this view yet.</p></section>
      ) : (
        <>
          <section className="section">
            <SectionHead kicker="Against chance" title={s.excess >= 0 ? `${s.excess.toFixed(1)} hits above what chance predicts` : `${Math.abs(s.excess).toFixed(1)} hits below what chance predicts`}
              body={`${s.hits} of ${s.n} is ${pct(s.rate, 1)}, and the likely range for the true rate is ${pct(s.ci.lo)} to ${pct(s.ci.hi)}.${s.permP !== null ? ` A shuffle test that ignores any colour habits gives p = ${fmtP(s.permP)}.` : ""}`} />
            <div className="two">
              <figure className="figure">
                <figcaption><b>Running score above chance</b><span>Inside the shaded band is what luck alone usually produces.</span></figcaption>
                <PathChart slice={s} />
              </figure>
              <figure className="figure">
                <figcaption><b>Evidence score</b><span>Reaches {verdict.bar} by chance at most 1 time in {verdict.bar}, however often you check.</span></figcaption>
                <EvidenceChart slice={s} bar={verdict.bar} />
              </figure>
            </div>
          </section>

          <section className="section">
            <SectionHead kicker="Over time" title={s.trend.p < 0.05 ? (s.trend.slope > 0 ? "Your rate is climbing" : "Your rate is falling") : "No clear trend yet"}
              body={`Trend test across all trials: p = ${fmtP(s.trend.p)}. Rate is shown per session, with a 30-trial rolling line.`} />
            <figure className="figure wide">
              <SessionsChart slice={s} />
              <details className="table-toggle"><summary>Table</summary><SessionsTable slice={s} /></details>
            </figure>
            <figure className="figure wide">
              <figcaption><b>Every card, in order</b><span>Top half is the card, bottom half is the answer. A solid tile is a hit. The last {s.ribbon.length} cards are shown.</span></figcaption>
              <TrialRibbon ribbon={s.ribbon} />
            </figure>
          </section>

          <section className="section">
            <SectionHead kicker="By colour" title={colorTitle(s)}
              body="Hit rate alone flatters whichever colour you say most: always say red and you score 100% on red. Lift fixes that by asking whether you say a colour more when it's actually up. Zero means it tracks nothing." />
            <div className="two">
              <figure className="figure">
                <figcaption><b>Hit rate by card</b><span>The tick marks chance.</span></figcaption>
                <ColorRows slice={s} />
              </figure>
              <figure className="figure">
                <figcaption><b>What you said when each card was up</b><span>Rows are the card, columns the answer. The diagonal is hits.</span></figcaption>
                <Confusion slice={s} />
              </figure>
            </div>
            {s.warmCool && (
              <p className="aside">Warm versus cool, a sensation some trainers describe: you matched temperature on {pct(s.warmCool.rate)} of {s.warmCool.n} trials, against {pct(s.warmCool.expected)} expected from your own habits (p = {fmtP(s.warmCool.p)}).</p>
            )}
          </section>

          <section className="section">
            <SectionHead kicker="Sequence" title={s.sequence.p < 0.05 ? (s.sequence.afterHit.rate > s.sequence.afterMiss.rate ? "A hit tends to follow a hit" : "A hit tends to follow a miss") : "One card doesn't predict the next"}
              body={`Within turns, you hit ${pct(s.sequence.afterHit.rate)} right after a hit and ${pct(s.sequence.afterMiss.rate)} right after a miss (p = ${fmtP(s.sequence.p)}).`} />
            <div className="two">
              <figure className="figure">
                <figcaption><b>Through a turn</b><span>Do you warm up or fade? First half vs second half: p = {fmtP(s.sequence.halfP)}.</span></figcaption>
                <RateRows rows={s.sequence.positions} chance={s.chance} />
              </figure>
              <figure className="figure">
                <figcaption><b>Patterns in your answers</b><span>Random cards repeat at the chance rate. Minds tend not to.</span></figcaption>
                <dl className="stats">
                  <div><dt>You repeat your last answer</dt><dd>{pct(s.sequence.guessRepeat.rate)}</dd></div>
                  <div><dt>The same card comes up twice</dt><dd>{pct(s.sequence.targetRepeat.rate)}</dd></div>
                  <div><dt>You say the card you just heard</dt><dd>{pct(s.sequence.guessFollowsTarget.rate)}</dd></div>
                  <div><dt>Expected by chance</dt><dd>{pct(s.sequence.expected)}</dd></div>
                </dl>
              </figure>
            </div>
          </section>

          <section className="section">
            <SectionHead kicker="Experience" title={experienceTitle(s)}
              body={`Ginette separates knowing from seeing, so the app does too: "see red", "feel red", or just "red".${s.experience.sawVsGuessP !== null ? ` Saw vs guess: p = ${fmtP(s.experience.sawVsGuessP)}.` : ""}`} />
            <div className="two">
              <figure className="figure">
                <figcaption><b>Hit rate by what you reported</b></figcaption>
                <RateRows rows={[{ label: "Saw it", ...s.experience.saw }, { label: "Felt it", ...s.experience.felt }, { label: "Guessed", ...s.experience.guess }]} chance={s.chance} />
              </figure>
              <figure className="figure">
                <figcaption><b>Hit rate by furthest stage reached that turn</b><span>From the journal after each turn.</span></figcaption>
                {s.stages.length ? <RateRows rows={s.stages} chance={s.chance} /> : <p className="empty">Pick a stage after each turn to fill this in.</p>}
              </figure>
            </div>
            <div className="two">
              <figure className="figure">
                <figcaption><b>Hit rate by state</b><span>From the chips you pick before a turn.</span></figcaption>
                {s.tags.length ? <RateRows rows={s.tags.map((t) => ({ label: t.tag, ...t }))} chance={s.chance} /> : <p className="empty">No state tags yet.</p>}
              </figure>
              <figure className="figure">
                <figcaption><b>Hit rate by time of day</b><span>Mountain time. {s.latency.medianMs ? `Median answer: ${(s.latency.medianMs / 1000).toFixed(1)}s.` : ""}</span></figcaption>
                <RateRows rows={s.times} chance={s.chance} />
              </figure>
            </div>
          </section>
        </>
      )}

      <section className="section coach">
        <SectionHead kicker="Coaching" title="What to try next"
          body="These compare a lot of things on one dataset, so treat them as ideas to test, not findings. None of them feed the verdict." />
        <div className="insights" data-testid="insights">
          {data.insights.map((i) => (
            <article key={i.title} className={`insight ${i.kind}`}>
              <p className="insight-kind">{i.kind === "lead" ? "A lead to test" : i.kind === "check" ? "Worth checking" : "Note"} · {i.n} trials</p>
              <h3>{i.title}</h3>
              <p>{i.body}</p>
            </article>
          ))}
        </div>
      </section>

      <Controls data={data} />

      <section className="section">
        <SectionHead kicker="How long" title="How many trials this could take"
          body={`Median trials for the evidence score to reach ${data.bar.evidence} at a given true hit rate. These are simulated, and they assume a real effect exists.`} />
        <div className="power">
          {data.power.map((p) => (
            <div key={p.rate}><span className="power-rate">{pct(p.rate)}</span><span className="power-n">{p.trials === null ? "6,000+" : p.trials.toLocaleString("en-US")}</span><span className="power-unit">trials</span></div>
          ))}
        </div>
      </section>

      <section className="section">
        <details className="log">
          <summary><span className="eyebrow">Trial log</span> Latest {data.log.length}, including voids, controls and corrections</summary>
          <div className="table-wrap">
            <table className="table small" data-testid="log">
              <thead><tr><th>When</th><th>Who</th><th>Type</th><th>Card</th><th>Answer</th><th>Result</th><th>Report</th><th>Heard</th></tr></thead>
              <tbody>
                {data.log.map((t) => (
                  <tr key={t.id} className={t.voided ? "void" : ""}>
                    <td className="nowrap">{new Date(t.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                    <td>{t.who}</td>
                    <td>{t.voided ? "void" : t.condition === "sham" ? "dark" : t.kind}{t.corrected ? ", noted misheard" : ""}</td>
                    <td><Swatch c={t.target} /></td><td><Swatch c={t.guess} /></td>
                    <td>{t.voided ? "–" : t.hit ? "Hit" : "Miss"}</td>
                    <td>{t.experience ?? "–"}</td>
                    <td className="transcript">{t.transcript ?? (t.source ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </section>
    </main>
  );
}

function colorTitle(s: Slice): string {
  const leader = s.colors.filter((c) => c.pAdj < 0.1 && c.lift > 0).sort((a, b) => b.lift - a.lift)[0];
  return leader ? `${leader.label} may be coming through` : "No colour stands out yet";
}

function experienceTitle(s: Slice): string {
  const { saw, guess, sawVsGuessP } = s.experience;
  if (saw.n < 5) return "Not enough \"I see\" reports yet";
  if (sawVsGuessP !== null && sawVsGuessP < 0.05 && saw.rate > guess.rate) return "\"I see\" scores higher than guessing";
  return "\"I see\" isn't beating guesses yet";
}

function SectionHead({ kicker, title, body }: { kicker: string; title: string; body?: string }) {
  return (
    <header className="section-head">
      <p className="eyebrow">{kicker}</p>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
    </header>
  );
}

function Swatch({ c }: { c: string | null }) {
  if (!c) return <span className="muted">–</span>;
  const d = COLORS[c as ColorId];
  return <span className="swatch"><i style={{ background: d.ui }} />{d.label}</span>;
}

// ---- verdict pieces --------------------------------------------------------

function Meter({ value, bar }: { value: number; bar: number }) {
  // Log scale from 1/100 to 1000. The middle tick is 1, where the score started.
  const lo = -2, hi = 3;
  const at = (v: number) => ((Math.log10(Math.max(1e-2, Math.min(1e3, v))) - lo) / (hi - lo)) * 100;
  return (
    <span className="meter-e" role="img" aria-label={`Evidence ${fmtE(value)}, bar at ${bar}`}>
      <span className="meter-track" />
      <span className="meter-tick" style={{ left: `${at(1)}%` }} />
      <span className="meter-bar" style={{ left: `${at(bar)}%` }} />
      <span className="meter-dot" style={{ left: `${at(value)}%` }} />
    </span>
  );
}

function Ladder({ v, nav }: { v: Verdict; nav: Nav }) {
  return (
    <section className="ladder" data-testid="ladder">
      <div className="ladder-head">
        <p className="eyebrow">What it takes to believe this</p>
        <a className="linkish" href="/protocol" onClick={(e) => { e.preventDefault(); nav("/protocol"); }}>Read the protocol</a>
      </div>
      <ol>
        {v.ladder.map((s, i) => (
          <li key={s.id} className={`step ${s.status}`}>
            <span className="step-n"><Icon kind={s.status} /></span>
            <div>
              <h3><span className="step-i">{i + 1}</span>{s.label}</h3>
              <p>{s.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---- charts ----------------------------------------------------------------

function useWidth<T extends HTMLElement>(initial = 560) {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(initial);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => { if (e && e.contentRect.width > 0) setW(e.contentRect.width); });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

function niceTicks(lo: number, hi: number, n = 4): number[] {
  const range = hi - lo || 1;
  const raw = range / n;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(+v.toFixed(6));
  return out;
}

interface Hover { x: number; y: number; html: React.ReactNode }

function Tooltip({ hover }: { hover: Hover | null }) {
  if (!hover) return null;
  return <div className="tip" style={{ left: hover.x, top: hover.y }} role="status">{hover.html}</div>;
}

const M = { t: 12, r: 14, b: 26, l: 40 };

function PathChart({ slice }: { slice: Slice }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<Hover | null>(null);
  const [mark, setMark] = useState<number | null>(null);
  const H = 240;
  const pts = slice.path;
  const iw = W - M.l - M.r, ih = H - M.t - M.b;
  const maxX = pts.at(-1)?.i ?? 1;
  const maxY = Math.max(5, ...pts.map((p) => Math.max(Math.abs(p.dev), p.band))) * 1.08;
  const x = (v: number) => M.l + (v / maxX) * iw;
  const y = (v: number) => M.t + ((maxY - v) / (2 * maxY)) * ih;
  const line = pts.map((p, i) => `${i ? "L" : "M"}${x(p.i).toFixed(1)},${y(p.dev).toFixed(1)}`).join("");
  const band = `M${x(0)},${y(0)}` + pts.map((p) => `L${x(p.i).toFixed(1)},${y(p.band).toFixed(1)}`).join("") + pts.slice().reverse().map((p) => `L${x(p.i).toFixed(1)},${y(-p.band).toFixed(1)}`).join("") + "Z";
  const last = pts.at(-1);
  const onMove = (e: React.PointerEvent) => {
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const target = ((px - M.l) / iw) * maxX;
    let best = pts[0]!;
    for (const p of pts) if (Math.abs(p.i - target) < Math.abs(best.i - target)) best = p;
    setMark(best.i);
    setHover({ x: x(best.i), y: y(best.dev), html: <><b>Trial {best.i}</b><br />{best.dev >= 0 ? "+" : ""}{best.dev.toFixed(1)} vs chance<br />Luck band ±{best.band.toFixed(1)}</> });
  };
  return (
    <div ref={ref} className="chart">
      <svg width={W} height={H} role="img" aria-label={`Running score, ending ${last?.dev.toFixed(1)} above chance after ${maxX} trials`}
        onPointerMove={onMove} onPointerLeave={() => { setHover(null); setMark(null); }}>
        <path d={band} className="band" />
        {niceTicks(-maxY, maxY).map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} className={t === 0 ? "axis" : "grid"} />
            <text x={M.l - 8} y={y(t) + 4} textAnchor="end" className="tick">{t > 0 ? `+${t}` : t}</text>
          </g>
        ))}
        {niceTicks(0, maxX).map((t) => <text key={t} x={x(t)} y={H - 7} textAnchor="middle" className="tick">{t}</text>)}
        <path d={line} className="series" />
        {last && <circle cx={x(last.i)} cy={y(last.dev)} r={4} className="end" />}
        {mark !== null && <line x1={x(mark)} x2={x(mark)} y1={M.t} y2={H - M.b} className="cross" />}
      </svg>
      <Tooltip hover={hover} />
    </div>
  );
}

function EvidenceChart({ slice, bar }: { slice: Slice; bar: number }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<Hover | null>(null);
  const [mark, setMark] = useState<number | null>(null);
  const H = 240;
  const pts = slice.path;
  const iw = W - M.l - M.r, ih = H - M.t - M.b;
  const maxX = pts.at(-1)?.i ?? 1;
  const lo = Math.min(-1, Math.floor(Math.min(...pts.map((p) => Math.log10(Math.max(p.e, 1e-4)))))), hi = Math.max(2.4, Math.ceil(Math.max(...pts.map((p) => Math.log10(Math.max(p.e, 1e-4))))));
  const x = (v: number) => M.l + (v / maxX) * iw;
  const y = (v: number) => M.t + ((hi - v) / (hi - lo)) * ih;
  const line = pts.map((p, i) => `${i ? "L" : "M"}${x(p.i).toFixed(1)},${y(Math.log10(Math.max(p.e, 1e-4))).toFixed(1)}`).join("");
  const ticks: number[] = [];
  for (let v = lo; v <= hi; v++) ticks.push(v);
  const onMove = (e: React.PointerEvent) => {
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const target = ((e.clientX - rect.left - M.l) / iw) * maxX;
    let best = pts[0]!;
    for (const p of pts) if (Math.abs(p.i - target) < Math.abs(best.i - target)) best = p;
    setMark(best.i);
    setHover({ x: x(best.i), y: y(Math.log10(Math.max(best.e, 1e-4))), html: <><b>Trial {best.i}</b><br />Evidence {fmtE(best.e)}</> });
  };
  return (
    <div ref={ref} className="chart">
      <svg width={W} height={H} role="img" aria-label={`Evidence score, now ${fmtE(pts.at(-1)?.e ?? 1)}`}
        onPointerMove={onMove} onPointerLeave={() => { setHover(null); setMark(null); }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} className={t === 0 ? "axis" : "grid"} />
            <text x={M.l - 8} y={y(t) + 4} textAnchor="end" className="tick">{t >= 0 ? 10 ** t : `1/${10 ** -t}`}</text>
          </g>
        ))}
        <line x1={M.l} x2={W - M.r} y1={y(Math.log10(bar))} y2={y(Math.log10(bar))} className="bar-line" />
        <text x={W - M.r} y={y(Math.log10(bar)) - 6} textAnchor="end" className="bar-label">The bar: {bar}</text>
        {niceTicks(0, maxX).map((t) => <text key={t} x={x(t)} y={H - 7} textAnchor="middle" className="tick">{t}</text>)}
        <path d={line} className="series" />
        {pts.at(-1) && <circle cx={x(pts.at(-1)!.i)} cy={y(Math.log10(Math.max(pts.at(-1)!.e, 1e-4)))} r={4} className="end" />}
        {mark !== null && <line x1={x(mark)} x2={x(mark)} y1={M.t} y2={H - M.b} className="cross" />}
      </svg>
      <Tooltip hover={hover} />
    </div>
  );
}

function SessionsChart({ slice }: { slice: Slice }) {
  const [ref, W] = useWidth<HTMLDivElement>(900);
  const [hover, setHover] = useState<Hover | null>(null);
  const H = 220;
  const iw = W - M.l - M.r, ih = H - M.t - M.b;
  const N = slice.sessions.length;
  const totalTrials = slice.n;
  const maxRate = Math.min(1, Math.max(0.5, ...slice.sessions.map((s) => s.rate)) + 0.05);
  const x = (trial: number) => M.l + (trial / Math.max(1, totalTrials)) * iw;
  const y = (r: number) => M.t + (1 - r / maxRate) * ih;
  let cum = 0;
  const dots = slice.sessions.map((s) => {
    const mid = cum + s.n / 2;
    cum += s.n;
    return { ...s, mid };
  });
  const roll = slice.rolling.map((p, i) => `${i ? "L" : "M"}${x(p.i).toFixed(1)},${y(p.rate).toFixed(1)}`).join("");
  return (
    <div ref={ref} className="chart">
      <svg width={W} height={H} role="img" aria-label={`Hit rate across ${N} sessions`}>
        {niceTicks(0, maxRate, 4).map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} className={t === 0 ? "axis" : "grid"} />
            <text x={M.l - 8} y={y(t) + 4} textAnchor="end" className="tick">{Math.round(t * 100)}%</text>
          </g>
        ))}
        <line x1={M.l} x2={W - M.r} y1={y(slice.chance)} y2={y(slice.chance)} className="chance" />
        <text x={W - M.r} y={y(slice.chance) - 6} textAnchor="end" className="bar-label">Chance {pct(slice.chance)}</text>
        <text x={M.l} y={H - 7} className="tick">First session</text>
        <text x={W - M.r} y={H - 7} textAnchor="end" className="tick">Latest</text>
        {roll && <path d={roll} className="series soft" />}
        {dots.map((d) => (
          <g key={d.id}>
            <circle cx={x(d.mid)} cy={y(d.rate)} r={Math.max(4, Math.min(9, Math.sqrt(d.n)))} className="dot" />
            <circle cx={x(d.mid)} cy={y(d.rate)} r={14} fill="transparent"
              onPointerEnter={() => setHover({ x: x(d.mid), y: y(d.rate), html: <><b>{new Date(d.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</b><br />{d.hits} of {d.n}, {pct(d.rate)}</> })}
              onPointerLeave={() => setHover(null)} />
          </g>
        ))}
      </svg>
      <Tooltip hover={hover} />
    </div>
  );
}

function SessionsTable({ slice }: { slice: Slice }) {
  return (
    <div className="table-wrap"><table className="table small">
      <thead><tr><th>Date</th><th>Trials</th><th>Hits</th><th>Rate</th><th>Chance</th></tr></thead>
      <tbody>{slice.sessions.slice().reverse().map((s) => (
        <tr key={s.id}><td>{new Date(s.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td><td>{s.n}</td><td>{s.hits}</td><td>{pct(s.rate)}</td><td>{pct(s.chance)}</td></tr>
      ))}</tbody>
    </table></div>
  );
}

function TrialRibbon({ ribbon }: { ribbon: Slice["ribbon"] }) {
  return (
    <div className="ribbon" role="img" aria-label={`Last ${ribbon.length} trials as coloured tiles`}>
      {ribbon.map((r, i) => {
        const newSession = i > 0 && ribbon[i - 1]!.s !== r.s;
        const t = r.t ? COLORS[r.t].ui : "#333", g = r.g ? COLORS[r.g].ui : "#333";
        return (
          <span key={i} className={`rtile ${r.h ? "hit" : "miss"} ${newSession ? "gap" : ""}`}
            title={`Card ${r.t}, answer ${r.g}: ${r.h ? "hit" : "miss"}`}
            style={r.h ? { background: t } : { background: `linear-gradient(${t} 0 50%, ${g} 50% 100%)` }} />
        );
      })}
    </div>
  );
}

function ColorRows({ slice }: { slice: Slice }) {
  const scale = Math.max(0.6, ...slice.colors.map((c) => c.hi));
  return (
    <div className="rows">
      {slice.colors.map((c) => (
        <div key={c.id} className="row-chart" data-testid={`color-${c.id}`}>
          <span className="row-label"><i style={{ background: c.ui }} />{c.label}</span>
          <span className="row-plot">
            <span className="range" style={{ left: `${(c.lo / scale) * 100}%`, width: `${((c.hi - c.lo) / scale) * 100}%` }} />
            <span className="fill" style={{ width: `${(c.rate / scale) * 100}%`, background: c.ui }} />
            <span className="chance-tick" style={{ left: `${(slice.chance / scale) * 100}%` }} />
          </span>
          <span className="row-val"><b>{pct(c.rate)}</b> <small>{c.hits}/{c.n}</small></span>
          <span className="row-side" title="How much more you say this colour when it's actually up">{c.lift >= 0 ? "+" : ""}{Math.round(c.lift * 100)} lift{c.pAdj < 0.1 ? "*" : ""}</span>
        </div>
      ))}
      <p className="row-foot">You say each: {slice.colors.map((c) => `${c.label.toLowerCase()} ${pct(c.guessShare)}`).join(", ")}. * holds after correcting for testing several colours.</p>
    </div>
  );
}

function Confusion({ slice }: { slice: Slice }) {
  const { targets, guesses, counts } = slice.confusion;
  const rowTotals = counts.map((r) => r.reduce((a, b) => a + b, 0));
  return (
    <div className="confusion" style={{ gridTemplateColumns: `auto repeat(${guesses.length}, minmax(0, 1fr))` }}>
      <span />
      {guesses.map((g) => <span key={g} className="cf-head">{COLORS[g].label}</span>)}
      {targets.map((t, i) => (
        <div key={t} className="cf-row">
          <span className="cf-side"><i style={{ background: COLORS[t].ui }} />{COLORS[t].label}</span>
          {guesses.map((g, j) => {
            const share = rowTotals[i] ? counts[i]![j]! / rowTotals[i]! : 0;
            const isHit = t === g;
            return (
              <span key={g} className={isHit ? "cf-cell hit" : "cf-cell"} style={{ background: isHit ? `color-mix(in srgb, ${COLORS[t].ui} ${Math.round(share * 110)}%, #1c1a17)` : `rgba(233,184,114,${(share * 0.75).toFixed(3)})`, color: isHit && share > 0.55 ? textOn(COLORS[t].ui) : undefined }} title={`${COLORS[t].label} up, said ${COLORS[g].label}: ${counts[i]![j]} of ${rowTotals[i]}`}>
                {Math.round(share * 100)}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function RateRows({ rows, chance }: { rows: { label: string; n: number; hits: number; rate: number; lo: number; hi: number }[]; chance: number }) {
  const scale = Math.max(0.6, ...rows.map((r) => r.hi));
  return (
    <div className="rows">
      {rows.map((r) => (
        <div key={r.label} className="row-chart slim">
          <span className="row-label plain">{r.label}</span>
          <span className="row-plot">
            {r.n > 0 && <span className="range" style={{ left: `${(r.lo / scale) * 100}%`, width: `${((r.hi - r.lo) / scale) * 100}%` }} />}
            {r.n > 0 && <span className="pip" style={{ left: `${(r.rate / scale) * 100}%` }} />}
            <span className="chance-tick" style={{ left: `${(chance / scale) * 100}%` }} />
          </span>
          <span className="row-val">{r.n ? <><b>{pct(r.rate)}</b> <small>{r.hits}/{r.n}</small></> : <small>none</small>}</span>
        </div>
      ))}
    </div>
  );
}

// ---- controls --------------------------------------------------------------

function Status({ ok, warn, label }: { ok: boolean; warn?: boolean; label: string }) {
  return <span className={`status ${ok ? "good" : warn ? "warn" : "bad"}`}><Icon kind={ok ? "done" : warn ? "todo" : "failed"} />{label}</span>;
}

function Controls({ data }: { data: Analysis }) {
  const c = data.controls;
  const voidShare = c.voids.ofTotal ? c.voids.n / c.voids.ofTotal : 0;
  return (
    <section className="section" data-testid="controls">
      <SectionHead kicker="Controls" title="Can this result be trusted?" body="If any of these go wrong, fix it before trusting the verdict above." />
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Check</th><th>Result</th><th>Should be</th><th>Status</th></tr></thead>
          <tbody>
            <tr>
              <td><b>Hidden second draw</b><br /><small>Each answer also scored against a card nobody ever displayed.</small></td>
              <td>{c.controlTarget.n ? `${pct(c.controlTarget.rate)} of ${c.controlTarget.n}` : "No data"}</td>
              <td>{pct(c.controlTarget.chance)}, chance</td>
              <td>{c.controlTarget.n < 30 ? <Status ok={false} warn label="Needs 30" /> : <Status ok={c.controlTarget.p > 0.01} label={c.controlTarget.p > 0.01 ? "At chance" : "Above chance"} />}</td>
            </tr>
            <tr>
              <td><b>Dark-screen trials</b><br /><small>A target was drawn, but the phone stayed dark.</small></td>
              <td>{c.dark.n ? `${pct(c.dark.rate)} of ${c.dark.n}` : "No data"}</td>
              <td>{pct(c.dark.chance)}, chance</td>
              <td>{c.dark.n < 30 ? <Status ok={false} warn label="Needs 30" /> : <Status ok={c.dark.p > 0.01} label={c.dark.p > 0.01 ? "At chance" : "Above chance"} />}</td>
            </tr>
            <tr>
              <td><b>"I see" when nothing's there</b><br /><small>How often you report a visual on dark trials vs real ones.</small></td>
              <td>{pct(c.dark.sawOnDark)} dark, {pct(c.dark.sawOnReal)} real</td>
              <td>Lower on dark</td>
              <td>{c.dark.n < 15 ? <Status ok={false} warn label="Too few" /> : <Status ok={c.dark.sawOnDark < c.dark.sawOnReal || c.dark.sawOnReal === 0} warn label={c.dark.sawOnDark < c.dark.sawOnReal ? "Lower on dark" : "No difference"} />}</td>
            </tr>
            {c.audits.length === 0 ? (
              <tr><td><b>Rig attack</b><br /><small>24 cards spent deliberately trying to cheat.</small></td><td>Not run</td><td>Chance</td><td><Status ok={false} warn label="Not run" /></td></tr>
            ) : c.audits.map((a) => (
              <tr key={a.guesser + a.rig}>
                <td><b>Rig attack: {a.rig}</b><br /><small>{a.name}, cheating on purpose.</small></td>
                <td>{a.hits} of {a.n}</td><td>{pct(a.chance)}, chance</td>
                <td>{a.status === "too few" ? <Status ok={false} warn label="Needs 24" /> : <Status ok={a.status === "passed"} label={a.status === "passed" ? "Held" : "Leaks"} />}</td>
              </tr>
            ))}
            {c.sighted.length === 0 ? (
              <tr><td><b>Eyes-open check</b><br /><small>Mask off, to prove the pipeline works.</small></td><td>Not run</td><td>90%+</td><td><Status ok={false} warn label="Not run" /></td></tr>
            ) : c.sighted.map((x) => (
              <tr key={x.guesser}><td><b>Eyes-open check</b><br /><small>{x.name}</small></td><td>{x.hits} of {x.n}</td><td>90%+</td><td><Status ok={x.passed} warn={x.n < 5} label={x.passed ? "Works" : x.n < 5 ? "Needs 5" : "Low"} /></td></tr>
            ))}
            <tr>
              <td><b>Card randomness</b><br /><small>Each colour should come up about equally.</small></td>
              <td>{c.uniformity.counts.map((u) => `${COLORS[u.id as ColorId].label.toLowerCase()} ${u.n}`).join(", ") || "No data"}</td>
              <td>Even, p &gt; .01</td>
              <td>{c.uniformity.n < 30 ? <Status ok={false} warn label="Too few" /> : <Status ok={c.uniformity.p > 0.01} label={c.uniformity.p > 0.01 ? "Even" : "Skewed"} />}</td>
            </tr>
            <tr>
              <td><b>Voids and corrections</b><br /><small>Voids happen before the answer. Corrections are notes, and the locked score stands.</small></td>
              <td>{c.voids.n} voided ({pct(voidShare)}), {c.corrections.n} noted as misheard, {c.corrections.wouldFlip} of those claiming a miss was a hit</td>
              <td>Low</td>
              <td><Status ok={voidShare < 0.1} warn label={voidShare < 0.1 ? "Low" : "High, look into it"} /></td>
            </tr>
            <tr>
              <td><b>How answers were captured</b><br /><small>Voice keeps a transcript. In physical-card mode, a partner's tap isn't blind.</small></td>
              <td>{c.sources.voice} voice, {c.sources.partner} partner, {c.sources.tap} own tap</td>
              <td>Mostly voice</td>
              <td><Status ok={c.sources.voice >= c.sources.partner + c.sources.tap} warn label={c.sources.voice >= c.sources.partner + c.sources.tap ? "Mostly voice" : "Mostly tapped"} /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="aside">Outside the verdict: {c.practice.n} practice trials at {pct(c.practice.rate)}, and {c.cards.n} physical-card trials at {pct(c.cards.rate)}{c.cards.permP !== null ? ` (shuffle test p = ${fmtP(c.cards.permP)}, which assumes a bag draw with replacement: counting a closed deck can fake a low p)` : ""}.</p>
    </section>
  );
}
